import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { RetentionLineDTO } from '../../dto/HonorariumLiquidationDTO';
import { formatDateOnly } from '../../utils/date';
import { formatMoney } from '../../utils/money';
import MovementTypeTag from './MovementTypeTag';
import SignedAmount from './SignedAmount';

interface RetentionLinesTableProps {
    lines: RetentionLineDTO[];
    emptyMessage?: string;
}

/** "420101 · CONSULTA MEDICA", o lo que haya de los dos. */
function practice(row: RetentionLineDTO): string {
    const parts = [row.nomenclatorCode, row.nomenclatorDescription].filter(part => part && part.trim());
    return parts.length > 0 ? parts.join(' · ') : '—';
}

/**
 * Movimientos de las órdenes que facturó el profesional como emisor, con lo que se le retiene: el monto menos su
 * parte según el contrato de distribución. Una retención negativa es una anulación de una orden ya retenida: se le
 * devuelve. La columna "Atendió" aparece solo si alguna práctica la atendió otro profesional.
 */
export default function RetentionLinesTable({ lines, emptyMessage = 'Sin retenciones.' }: RetentionLinesTableProps) {
    const attendedByOther = lines.some(line => line.professional !== line.issuer);
    return (
        <DataTable value={lines} className="crudTable" emptyMessage={emptyMessage}>
            <Column header="Fecha" body={(row: RetentionLineDTO) => formatDateOnly(row.date)} style={{ width: '110px' }} />
            <Column
                header="Orden"
                body={(row: RetentionLineDTO) => row.benefitOrder ?? '—'}
                className="text-center"
                headerClassName="col-center"
                style={{ width: '100px' }}
            />
            <Column
                header="Tipo"
                body={(row: RetentionLineDTO) => <MovementTypeTag movementType={row.movementType} detail={row.movementDetail} />}
                style={{ width: '140px' }}
            />
            <Column header="Paciente" body={(row: RetentionLineDTO) => row.patientName || '—'} />
            <Column header="Práctica" body={practice} />
            {attendedByOther && (
                <Column header="Atendió" body={(row: RetentionLineDTO) => row.professionalName || '—'} />
            )}
            <Column
                header="Monto"
                body={(row: RetentionLineDTO) => <SignedAmount amount={row.amount} format={formatMoney} />}
                className="text-right"
                headerClassName="col-right"
                style={{ width: '140px' }}
            />
            <Column
                header="% retenido"
                body={(row: RetentionLineDTO) => row.retainedPercentage != null
                    ? `${row.retainedPercentage.toLocaleString('es-AR', { maximumFractionDigits: 2 })} %`
                    : '—'}
                className="text-center"
                headerClassName="col-center"
                style={{ width: '110px' }}
            />
            <Column
                header="Parte prof."
                body={(row: RetentionLineDTO) => <SignedAmount amount={row.professionalAmount} format={formatMoney} />}
                className="text-right"
                headerClassName="col-right"
                style={{ width: '140px' }}
            />
            <Column
                header="Retención"
                body={(row: RetentionLineDTO) => <SignedAmount amount={row.retentionAmount} format={formatMoney} strong />}
                className="text-right"
                headerClassName="col-right"
                style={{ width: '140px' }}
            />
        </DataTable>
    );
}
